import React, { useMemo, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import StepPersonal from './steps/StepPersonal'
import StepIncome from './steps/StepIncome'
import StepResults from './steps/StepResults'
import { useWizardStore } from './store'

const steps = [
  { id: 1, title: 'Your Tax Profile', component: StepPersonal },
  { id: 2, title: 'Income & Benefits', component: StepIncome },
  { id: 3, title: 'Results', component: StepResults },
]

export default function Wizard() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const stepFromQuery = Number(params.get('step') || '1')
  const stepIndex = Math.min(Math.max(stepFromQuery, 1), steps.length) - 1
  const StepComponent = steps[stepIndex].component
  const inputs = useWizardStore((s) => s.inputs)
  const selectedTaxYear = useWizardStore((s) => s.selectedTaxYear)
  const resetAll = useWizardStore((s) => s.resetAll)

  const canProceed = useMemo(() => {
    if (stepIndex === 0) return true
    if (stepIndex === 1) {
      const base = Number(inputs.baseSalary)
      return !isNaN(base) && base > 0
    }
    return true
  }, [stepIndex, inputs])

  // Ensure each step change scrolls the view back to the top
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [stepIndex])

  const go = (delta) => {
    const next = Math.min(Math.max(stepIndex + 1 + delta, 1), steps.length)
    setParams({ step: String(next) })
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">UK Salary Calculator</h1>
        <p className="text-sm text-gray-500">Tax Year {selectedTaxYear}</p>
      </div>

      <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4">
        {steps.map((s, i) => (
          <button
            key={s.id}
            className={`whitespace-nowrap px-3 py-2 rounded-full text-sm border transition ${i === stepIndex ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : (i < stepIndex ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-gray-700 border-gray-300')}`}
            onClick={() => setParams({ step: String(i + 1) })}
          >
            {s.title}
          </button>
        ))}
      </div>

      <div className="min-h-[360px] bg-white border rounded-lg p-4">
        <StepComponent />
      </div>

      <div className="sticky bottom-0 bg-white/80 backdrop-blur border-t mt-6">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center gap-2">
          <button
            className="px-4 py-2 rounded border border-gray-300 text-gray-700"
            onClick={() => go(-1)}
            disabled={stepIndex === 0}
          >
            Previous
          </button>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-2 rounded border border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => { resetAll(); setParams({ step: '1' }) }}
              type="button"
            >
              Reset
            </button>
            {stepIndex === 2 && (
              <button
                className="px-3 py-2 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                onClick={() => window.dispatchEvent(new Event('openPensionDrawer'))}
                type="button"
              >
                Quick adjust pension %
              </button>
            )}
            <button
              className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
              onClick={() => (stepIndex === steps.length - 1 ? navigate('/wizard?step=1') : go(1))}
              disabled={!canProceed}
            >
              {stepIndex === steps.length - 1 ? 'Start Over' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
} 